import {
  MarketoFormInterface,
  Theme,
  MenuLink as themeMenuLink
} from '@lucid/gatsby-theme-lucid';
import {
  DemoRequestClientsJsonConnection,
  Drupal,
  Drupal_Entity,
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphMenuFeaturedItem,
  EnterpriseHeaderLogosJsonConnection,
  FileConnection,
  Maybe,
  ProductTemplatesJsonConnection,
  Scalars
} from 'generated/drupal-graphql';

import { base } from 'theme/base';
import { colors } from 'theme/colors';
import { fonts } from 'theme/typography';

interface ExtendedDrupal extends Drupal {
  nodeReference?: Drupal_Entity;
}

export interface PageContext {
  nid: string;
  langPrefix: string;
  LanguageId: string;
  i18nKey: string;
  pageType: string;
  extraData: string;
  entityChanged?: string;
  page?: number;
  pageCount?: number;
  pageSize?: number;
  hubAlias?: string;
  excludeFromSitemap?: boolean;
  documentationType?: string;
  categories?: string[];
  persona?: string;
  hideSsr: boolean;
}

export interface MenuLink extends themeMenuLink {
  type?: string;
  truncate?: boolean;
  description?: string;
  fieldDropdownTitle?: string;
  fieldDropdownDescription?: string;
  fieldLinkPhoneNumber?: string;
  fieldKey?: string;
  fieldBeaconElementDetail?: string;
  fieldKmEventName?: string;
  fieldMarketo?: {
    entity: Drupal_ParagraphMarketoForm;
  };
  fieldIsbigdropdown?: boolean;
  fieldAddonBlurb?: string;
  fieldThumbnail?: string;
  fieldProductLink?: boolean;
  fieldDisableLanguage?: boolean;
  fieldDropdownFeaturedItem?: {
    entity: {
      entityTranslations: Drupal_ParagraphMenuFeaturedItem[];
    };
  };
  links: MenuLink[];
  entityId?: string;
  entityUuid?: string;
  entityBundle?: string;
}

export interface Data {
  drupal: ExtendedDrupal;
  i18nData?: FileConnection;
  allDemoRequestClientsJson?: DemoRequestClientsJsonConnection;
  allEnterpriseHeaderLogosJson?: EnterpriseHeaderLogosJsonConnection;
  allProductTemplatesJson?: ProductTemplatesJsonConnection;
}

export interface TemplateProps {
  data: Data;
  pageContext: PageContext;
  location: {
    pathname: string;
    origin: string;
    search: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state: any;
  };
}

export interface ExtraData {
  marketoForms: { [key: string]: MarketoFormInterface };
}

export interface MarketoFormField {
  targetId?: Maybe<Scalars['Int']>;
  entity?: Maybe<Drupal_ParagraphMarketoForm>;
  targetRevisionId?: Maybe<Scalars['Int']>;
}

export function isDefined<T extends object>(
  input: null | undefined | T | string
): input is T {
  return input != null;
}

export enum Template {
  CaseStudy = 'case_study',
  CaseStudyHub = 'case_study_hub',
  Consideration = 'consideration',
  ConsiderationCompetitor = 'consideration-competitor',
  ConsiderationCompetitorPpc = 'consideration-competitor-ppc',
  ConsiderationPpc = 'consideration_ppc',
  DemoRequest = 'demo_request',
  Discovery = 'discovery',
  Documentation = 'documentation',
  Ebook = 'ebook',
  Enterprise = 'enterprise',
  EnterpriseRegistration = 'enterprise_registration',
  FifteenIntegration = 'fifteen_integration',
  FifteenIntegrationPpc = 'fifteen_integration_ppc',
  FifteenOg = 'fifteen_og',
  FifteenOgCompetitor = 'fifteen_og_competitor',
  FifteenOgPpc = 'fifteen_og_ppc',
  FormPage = 'form_page',
  FunFlowchart = 'fun_flowchart',
  FunFlowchartHub = 'fun_flowchart_hub',
  Homepage = 'homepage',
  IndividualRole = 'individual_role',
  IntegrationHub = 'integration_hub',
  Nineteen = 'nineteen',
  NineteenSolutions = 'nineteen_solutions',
  Partners = 'partners',
  ProductTemplate = 'product_template',
  ProductTemplateHub = 'product_template_hub',
  RolesHub = 'roles_hub',
  SolutionsHub = 'solutions_hub',
  Sunrise = 'sunrise',
  TourPage = 'tour_page',
  Webinar = 'webinar',
  WebinarHub = 'webinar_hub',
  WhitePaper = 'white_paper'
}

export enum WeekDay {
  Sunday = 0,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday
}

export interface Tracker {
  [key: string]:
    | Array<{
        entity?: {
          entityLabel?: string | null;
        } | null;
      } | null>
    | {
        entity?: {
          entityLabel?: string | null;
        } | null;
      }
    | undefined
    | null;
}

const baseTheme = { ...fonts, ...base, colors: { ...colors } };

export type LucidTheme = Omit<
  Theme,
  'breakpoints' | 'space' | 'zIndices' | 'radii' | 'shadows'
> &
  typeof baseTheme;
